import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock } from '@phosphor-icons/react';
import api from '../lib/api';

const formatRelative = (futureIso) => {
  if (!futureIso) return '';
  const ms = new Date(futureIso).getTime() - Date.now();
  if (ms <= 0) return 'now';
  const mins = Math.floor(ms / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  if (days >= 1) return `${days}d ${hours % 24}h`;
  if (hours >= 1) return `${hours}h ${mins % 60}m`;
  return `${mins}m`;
};

const MarketStatusWidget = () => {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get('/market/status');
        if (!cancelled) setStatus(res.data);
      } catch { /* ignore */ }
    };
    load();
    const id = setInterval(load, 60_000); // refresh every minute
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (!status) {
    return (
      <div className="glass-card p-4 h-full">
        <div className="h-3 w-16 bg-white/10 rounded animate-pulse mb-3" />
        <div className="h-6 w-24 bg-white/10 rounded animate-pulse" />
      </div>
    );
  }

  const isOpen = status.is_open;
  const eventLabel = status.next_event === 'open' ? 'Opens in' : 'Closes in';
  const dotColor = isOpen ? 'bg-emerald-400' : 'bg-zinc-500';
  const ringColor = isOpen ? 'bg-emerald-400/30' : 'bg-zinc-500/20';

  return (
    <motion.div
      whileHover={{ y: -2 }}
      className="glass-card p-4 h-full" data-testid="market-status-widget"
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
        <Clock size={11} weight="bold" /> Market Status
      </div>
      <div className="flex items-center gap-2.5">
        <span className="relative flex w-2.5 h-2.5">
          {isOpen && (
            <motion.span
              className={`absolute inset-0 rounded-full ${ringColor}`}
              animate={{ scale: [1, 2.4, 1], opacity: [0.6, 0, 0.6] }}
              transition={{ duration: 1.6, repeat: Infinity }}
            />
          )}
          <span className={`relative w-2.5 h-2.5 rounded-full ${dotColor}`} />
        </span>
        <span className={`font-unbounded text-lg font-bold ${isOpen ? 'text-emerald-300' : 'text-zinc-300'}`}>
          {isOpen ? 'Open' : 'Closed'}
        </span>
      </div>
      <p className="text-xs text-zinc-400 mt-2">
        {eventLabel} <span className="text-white font-semibold">{formatRelative(status.next_at)}</span>
      </p>
    </motion.div>
  );
};

export default MarketStatusWidget;
